<template>
  <div class="search-bar search-bar--negative-section-margin theme-dark">
    <div class="search-bar__inner">
      <div class="search-bar__description">
        {{ $textDictionary['downloadCenterSoftware.searchDescription'] ?? '' }}
      </div>
      <div
        ref="searchRootRef"
        class="search-bar__input"
        data-testid="software-center-search"
      >
        <div
          v-if="!store.softwareDownloads?.filters"
          style="display: flex; gap: 8px; flex-direction: column"
        >
          <LoadingSkeleton class="search-bar-loading" />
        </div>
        <SearchBox
          v-else
          ref="searchBoxRef"
          :search-query="store.query"
          :input-label-text="searchFieldLabel"
          :input-placeholder-text="searchFieldLabel"
          :submit-search-btn-text="submitSearchBtnText"
          :clear-search-btn-text="clearSearchBtnText"
          :clear-suggestion-item-btn-text="clearSuggestionItemBtnText"
          :suggestions-list-title="suggestionsListTitle"
          @update:search-query="onSearchQueryUpdate"
          @submit="submitSearchForm"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { LoadingSkeleton, SearchBox } from '@hypercodestudio/basler-components';
import { useDownloadStore } from '~/stores/downloadStore/softwareDownloadStore';
import { getDownloadCenterSearchQuery } from '~/utils/algolia/getDownloadCenterSearchQuery';

const route = useRoute();
const router = useRouter();

const { $textDictionary, $analytics } = useNuxtApp();

const store = useDownloadStore();
const searchBoxRef = ref<{ focus: () => void } | null>(null);
const searchRootRef = ref<HTMLElement | null>(null);
const { focusSearchField } = useFocusSearchField(searchBoxRef, searchRootRef);

const searchFieldLabel = computed(
  () =>
    $textDictionary.value['softwareCenter.searchPlaceholder'] ??
    'softwareCenter.searchPlaceholder'
);
const submitSearchBtnText = computed(
  () => $textDictionary.value['searchbox.submitSearchBtnText.label'] ?? 'Search'
);
const clearSearchBtnText = computed(
  () =>
    $textDictionary.value['searchbox.clearSearchBtnText.label'] ??
    'Clear search'
);
const clearSuggestionItemBtnText = computed(
  () =>
    $textDictionary.value['searchbox.clearSuggestionItemBtnText.label'] ??
    'Remove previous search for'
);
const suggestionsListTitle = computed(
  () =>
    $textDictionary.value['searchbox.suggestionsListTitle.label'] ??
    'Suggested results for your search'
);

onMounted(() => {
  if (route.query.query) {
    store.query = route.query.query.toString();
  }
  focusSearchField();
});

// The field mounts only after filters are available.
watch(
  () => Boolean(store.softwareDownloads?.filters),
  (ready) => {
    if (!ready) {
      return;
    }
    focusSearchField();
  }
);

function onSearchQueryUpdate(value: string) {
  store.query = value;
  clearSearchTerm();
}

function submitSearchForm() {
  void store.fetchSoftwareDownloads();
  const target = store.createUrlParams();
  void router.push({
    path: store.getSoftwareCenterListPath(),
    query: target.query
  });

  $analytics?.pushToDataLayer({
    action: 'software_search',
    category: 'software',
    event: 'search-software',
    label: store.query
  });
}

function clearSearchTerm() {
  if (!isEmpty(store.query)) {
    return;
  }

  // Ignore programmatic clears from route sync (e.g. modal close) — only refetch
  // when the user cleared the field while the URL still carries a search term.
  if (isEmpty(getDownloadCenterSearchQuery(route.query))) {
    return;
  }

  const target = store.createUrlParams();
  void router.replace({
    path: store.getSoftwareCenterListPath(),
    query: target.query,
    replace: true
  });
  void store.fetchSoftwareDownloads();
}
</script>

<style scoped>
.search-bar-loading {
  width: 100%;
  border-radius: 60px;
  height: 52px;
}
@media (min-width: 768px) {
  .search-bar-loading {
    border-radius: 36px;
    height: 72px;
  }
}
</style>
